/**
 * Storage and limits: what an organisation keeps, store by store, against its limits and the
 * retention that bounds each (`GET /admin/v1/organisation/storage`, counted hourly). Shown on
 * Billing's Usage tab on Cloud and on the Quotas page on Enterprise. A meter is a ring in the
 * fill's colour with the figure beside it in words; a state is a dot and a word, never a colour alone.
 * A list group (Layout.tsx): the total and the database's disk with a ring at the end, then a row
 * per store with its figure and its limit at the end.
 */

import { quotaAmountText, type OrganisationStorage, type StorageRow } from '@aioe/shared';

import { routes } from '../api/client.ts';
import { timeAgo, useLoad } from '../hooks.ts';
import { ListGroup, ListRow, StatusMark } from './ui.tsx';
import type { ArtTone } from './ui/Art.tsx';

export type Fill = 'room' | 'near' | 'full' | 'none' | 'unlimited';

/** A ring: the fill carries how full it is (accent, warning, danger), on a lighter track of the same colour. */
export function Ring({ fill, ratio }: { fill: Fill; ratio: number }) {
  const r = 15;
  const c = 2 * Math.PI * r;
  const colour = fill === 'full' ? 'text-danger' : fill === 'near' ? 'text-warning' : fill === 'none' ? 'text-muted' : 'text-accent';
  const shown = Math.min(1, Math.max(0, ratio));
  return (
    <svg viewBox="0 0 40 40" className={`size-10 shrink-0 -rotate-90 ${colour}`} aria-hidden="true">
      <circle cx="20" cy="20" r={r} fill="none" stroke="currentColor" strokeOpacity={0.18} strokeWidth="4" />
      {shown > 0 ? <circle cx="20" cy="20" r={r} fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeDasharray={`${Math.max(shown * c, 0.5)} ${c}`} /> : null}
    </svg>
  );
}

const fillOf = (state: 'ok' | 'near' | 'full', used: number, limit: number | null): { fill: Fill; ratio: number } =>
  limit === null ? { fill: 'unlimited', ratio: 0 } : { fill: state === 'full' ? 'full' : state === 'near' ? 'near' : 'room', ratio: limit > 0 ? used / limit : 1 };

const bytes = (n: number) => quotaAmountText('bytes', n);

const NOUN: Record<StorageRow['key'], [string, string]> = { backups: ['snapshot', 'snapshots'], assets: ['file', 'files'], evidence: ['document', 'documents'], exports: ['archive', 'archives'], invoices: ['invoice', 'invoices'], memory: ['item', 'items'], audit: ['row', 'rows'], flows: ['row', 'rows'], usage: ['row', 'rows'], runs: ['row', 'rows'], projects: ['record', 'records'], teams: ['record', 'records'], catalogue: ['record', 'records'], inbox: ['record', 'records'], organisation: ['record', 'records'] };

/** One store's figure: the bytes, and how many it is, with "about" where the logs are counted by rows. */
function figure(s: StorageRow): string {
  const [one, many] = NOUN[s.key];
  const n = s.count === undefined ? '' : ` in ${s.count.toLocaleString('en-AU')} ${s.count === 1 ? one : many}`;
  return `${s.estimated ? 'About ' : ''}${bytes(s.bytes)}${n}`;
}

const limitWords = (s: StorageRow) => {
  if (s.limitText) return s.limitText;
  if (s.limit === null) return 'No limit';
  return `Limit ${s.limitUnit === 'count' ? s.limit.toLocaleString('en-AU') : bytes(s.limit)}`;
};

const fullMark = (state: 'ok' | 'near' | 'full', full = 'At the limit') => (state === 'full' ? <StatusMark state="error" label={full} /> : state === 'near' ? <StatusMark state="medium" label="Nearly full" /> : undefined);

/**
 * The "Storage and limits" group: the total with a ring, then each store with what bounds it. A calm
 * group carries no tone; `tone` is accepted so the callers need not change.
 */
export function StoragePanel(_: { tone?: ArtTone }) {
  const storage = useLoad(() => routes.organisationStorage(), []);
  const s: OrganisationStorage | undefined = storage.data;
  // Only administrators read it; anyone else (or an organisation with nothing yet) simply has no section.
  if (!s) return null;
  const total = fillOf(s.total.state, s.total.bytes, s.total.limit);
  return (
    <ListGroup
      title="Storage and limits"
      description={`${s.limitsFrom === 'plan' ? 'The limits are your plan’s.' : 'The limits are the ones your administrators set in Quotas.'} Counted hourly${s.measuredAt ? `, last ${timeAgo(s.measuredAt)}` : ''}. Logs are counted by their rows at a fixed size each, so those figures are approximate.`}
    >
      <ListRow
        title="Everything kept"
        description={
          <>
            <span className="block tabular-nums">{s.total.limit === null ? `${bytes(s.total.bytes)}, no limit` : `${bytes(s.total.bytes)} of ${bytes(s.total.limit)}`}</span>
            {s.total.atLimit ? <span className="block">{s.total.atLimit}</span> : null}
          </>
        }
        status={fullMark(s.total.state)}
        compactControl
        control={<Ring {...total} />}
      />
      {s.database ? <DatabaseRow d={s.database} /> : null}
      {s.stores.map((row) => (
        <ListRow
          key={row.key}
          title={row.label}
          description={
            <>
              <span className="block">{row.what}</span>
              <span className="block">{row.retention}</span>
            </>
          }
          status={fullMark(row.state)}
          control={
            <span className="grid max-w-64 gap-0.5 text-end tabular-nums @max-[35rem]:max-w-none @max-[35rem]:text-start">
              <span className="text-sm">{figure(row)}</span>
              <span className="text-xs text-muted">{limitWords(row)}</span>
            </span>
          }
        />
      ))}
    </ListGroup>
  );
}

/** Enterprise: the whole database's size on disk against the disk the deployment gave it (deployment-wide). */
function DatabaseRow({ d }: { d: NonNullable<OrganisationStorage['database']> }) {
  const f = fillOf(d.state, d.bytes, d.allocatedBytes);
  return (
    <ListRow
      title="The database’s disk"
      description={
        <>
          <span className="block tabular-nums">{d.allocatedBytes === null ? `${bytes(d.bytes)} used; the disk’s size is not known` : `${bytes(d.bytes)} of ${bytes(d.allocatedBytes)}, ${bytes(Math.max(0, d.allocatedBytes - d.bytes))} free`}</span>
          {d.atLimit ? <span className="block">{d.atLimit}</span> : null}
          <span className="block">
            {d.allocatedBytes === null ? 'Set AIOE_DATABASE_ALLOCATED_GB to the disk your Postgres has, and this shows the room left. ' : ''}
            The whole deployment’s database, indexes included: it is larger than the stores below add up to.
          </span>
        </>
      }
      status={fullMark(d.state, 'Full')}
      compactControl
      control={<Ring {...f} />}
    />
  );
}
